/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { Formik, yupToFormErrors, Field as FormikField } from 'formik'
import ValidationSchema from './EditFormValidation'
import { DOC_TYPES_ALLOWED } from './EditFormConstants'
import { useServiceContext } from '../../../../root'
import styled from 'styled-components'
import Modal from '../../../../components/Modal'
import Field from '../../../../components/shared/Field'
import { ErrorMessageStyled } from '../../../../components/shared/ErrorMessage'
import Loader from '../../../../components/shared/Loader'
import { InfiniteScrollSelect } from '../../../../components/InfiniteScrollSelect/InfiniteScrollSelect'
import { getOptionsForAsyncPicker as getMetadataOptionsForAsyncPicker } from '../../../../services/api/metadata'
import { documentTypesApiService } from '../../../../services/api/api.documenttypes'

const HalfWidthDiv = styled.div`
  width: 50%;
  float: left;
  padding-right: 15px;
  box-sizing: border-box;
`

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 15px;
  &.w100p {
    width: 100%;
  }
  &.w80p {
    width: 80%;
  }
`

const RadioGroup = styled.div`
  margin-top: 10px;
  margin-bottom: 15px;
`

const LongDocumentNameStyle = styled.div`
  .css-1rhbuit-multiValue {
    max-width: 98%;
  }
`

interface EditFormProps {
  onSubmit: (values: any, actions: any) => Promise<any>
  doClose: () => void
  isEdit: boolean
  initialValues: any
  loading?: Promise<any> | null
}

const EditForm: React.FC<EditFormProps> = ({ onSubmit, doClose, isEdit, initialValues, loading }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const validationSchema = ValidationSchema()

  const closeDialog = () => {
    if (doClose) {
      doClose()
    }
  }

  // Async load function for metadata/properties picker
  const loadMetadataOptions = async (search: string, page: number) => {
    return await getMetadataOptionsForAsyncPicker(search, page)
  }

  // Async load function for document types picker
  const loadDocumentTypesOptions = async (search: string, page: number) => {
    const params = {
      name: search || undefined,
      pageStart: page,
      pageSize: 25
    }
    return await documentTypesApiService.getOptionsForAsyncPicker(search, page)
  }

  return (
    <div>
      <Formik
        validate={(values) => {
          return validationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => yupToFormErrors(err)
            )
        }}
        onSubmit={(values, actions) => {
          return onSubmit(values, actions)
        }}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnBlur={false}
      >
        {({ values, handleSubmit, setFieldValue }) => (
          <div>
            <Loader blocking={loading !== null}>
              <Modal.Body>
                <form className={'clearfix'}>
                  <HalfWidthDiv>
                    {/* Name field */}
                    <StyledFormGroup controlId="12a0f969-5cc8-4c1a-b29e-5de5adeffb53">
                      <Form.Label>{translate('METADATA_FORM_LABEL_NAME')}</Form.Label>
                      <Field
                        type="text"
                        name="name"
                        maxLength={255}
                        autoFocus
                        id="12a0f969-5cc8-4c1a-b29e-5de5adeffb53"
                      />
                      <ErrorMessageStyled name="name" />
                    </StyledFormGroup>

                    {/* Description field */}
                    <StyledFormGroup controlId="7d6921af-c0c7-4d38-9d12-67781ca26993">
                      <Form.Label>{translate('METADATA_FORM_LABEL_DESCRIPTION')}</Form.Label>
                      <Field type="text" name="description" maxLength={255} id="7d6921af-c0c7-4d38-9d12-67781ca26993" />
                      <ErrorMessageStyled name="description" />
                    </StyledFormGroup>

                    {/* Properties (metadataTemplates) field */}
                    <StyledFormGroup controlId="b6aa12a5-d868-4fab-98a3-ad5f142fc807">
                      <Form.Label>{translate('PROPERTIES')}</Form.Label>
                      <InfiniteScrollSelect
                        menuPortalTarget={document.body}
                        placeholder={translate('SELECT_PROPERTIES...')}
                        name="metadataTemplates"
                        value={values.metadataTemplates}
                        asyncLoadFn={loadMetadataOptions}
                        onChange={(option: any) => {
                          setFieldValue('metadataTemplates', option || [])
                        }}
                        isMulti={true}
                        searchable={true}
                        id="b6aa12a5-d868-4fab-98a3-ad5f142fc807"
                      />
                      <ErrorMessageStyled name="metadataTemplates" />
                    </StyledFormGroup>
                  </HalfWidthDiv>

                  <div style={{ clear: 'both' }}></div>

                  {/* Document Types Allowed radio buttons */}
                  <Form.Label>{translate('SETTINGS_FOLDER_DOC_TYPE')}</Form.Label>

                  <RadioGroup>
                    <div className="radio">
                      <label htmlFor={`ad3fd64a-7276-4c3b-9dff-b1cab54c1a1b-${DOC_TYPES_ALLOWED.ALL}`}>
                        <FormikField
                          type="radio"
                          name="documentTypesAllowed"
                          value={DOC_TYPES_ALLOWED.ALL}
                          checked={values.documentTypesAllowed === DOC_TYPES_ALLOWED.ALL}
                          id={`ad3fd64a-7276-4c3b-9dff-b1cab54c1a1b-${DOC_TYPES_ALLOWED.ALL}`}
                        />
                        {translate('ALL')}
                      </label>
                    </div>

                    <div className="radio">
                      <label htmlFor={`ad3fd64a-7276-4c3b-9dff-b1cab54c1a1b-${DOC_TYPES_ALLOWED.DEFINED_IN_CREATION}`}>
                        <FormikField
                          type="radio"
                          name="documentTypesAllowed"
                          value={DOC_TYPES_ALLOWED.DEFINED_IN_CREATION}
                          checked={values.documentTypesAllowed === DOC_TYPES_ALLOWED.DEFINED_IN_CREATION}
                          id={`ad3fd64a-7276-4c3b-9dff-b1cab54c1a1b-${DOC_TYPES_ALLOWED.DEFINED_IN_CREATION}`}
                        />
                        {translate('SETTINGS_FOLDER_SELECT')}
                      </label>
                    </div>

                    {/* Conditional document types picker */}
                    {values.documentTypesAllowed === DOC_TYPES_ALLOWED.DEFINED_IN_CREATION && (
                      <StyledFormGroup controlId="5c774762-5f39-4479-83a4-58c0a962988f">
                        <LongDocumentNameStyle>
                          <InfiniteScrollSelect
                            menuPortalTarget={document.body}
                            placeholder={translate('SELECT_DOC_TYPES_PLACEHOLDER...')}
                            name="allowedDocumentTypeTemplates"
                            value={values.allowedDocumentTypeTemplates}
                            asyncLoadFn={loadDocumentTypesOptions}
                            onChange={(option: any) => {
                              setFieldValue('allowedDocumentTypeTemplates', option || [])
                            }}
                            isMulti={true}
                            searchable={true}
                            id="5c774762-5f39-4479-83a4-58c0a962988f"
                          />
                        </LongDocumentNameStyle>
                        <ErrorMessageStyled name="allowedDocumentTypeTemplates" />
                      </StyledFormGroup>
                    )}

                    <div className="radio">
                      <label htmlFor={`ad3fd64a-7276-4c3b-9dff-b1cab54c1a1b-${DOC_TYPES_ALLOWED.DEFINED_ON_FOLDER}`}>
                        <FormikField
                          type="radio"
                          name="documentTypesAllowed"
                          value={DOC_TYPES_ALLOWED.DEFINED_ON_FOLDER}
                          checked={values.documentTypesAllowed === DOC_TYPES_ALLOWED.DEFINED_ON_FOLDER}
                          id={`ad3fd64a-7276-4c3b-9dff-b1cab54c1a1b-${DOC_TYPES_ALLOWED.DEFINED_ON_FOLDER}`}
                        />
                        {translate('SETTINGS_FOLDER_SETUP_LABEL')}
                      </label>
                    </div>
                  </RadioGroup>

                  {/* Allow Certified Copies checkbox */}
                  <StyledFormGroup controlId="7d40391a-cea2-497c-a5a1-7b7ca0a82842">
                    <div className="checkbox">
                      <label htmlFor="7d40391a-cea2-497c-a5a1-7b7ca0a82842">
                        <FormikField
                          type="checkbox"
                          name="allowCertifiedCopies"
                          checked={!!values.allowCertifiedCopies}
                          id="7d40391a-cea2-497c-a5a1-7b7ca0a82842"
                        />
                        <b>{translate('SETTINGS_FOLDER_TYPE_ALLOW_CERTIFIED_COPY')}</b>
                      </label>
                    </div>
                  </StyledFormGroup>
                </form>
              </Modal.Body>
              <Modal.Footer>
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={closeDialog}
                  id="ca4d8e25-c0b0-4150-a1bc-39d55b0da29d"
                  data-action="cancel"
                >
                  {translate('CANCEL')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary text-capitalize"
                  onClick={() => handleSubmit()}
                  id="7a234f3f-47e6-4b9e-a147-2bafdd6e97ad"
                  data-action="save"
                >
                  {translate('SAVE')}
                </button>
              </Modal.Footer>
            </Loader>
          </div>
        )}
      </Formik>
    </div>
  )
}

export default EditForm
